import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AuthLayout from "@/pages/auth/components/AuthLayout";
import { supabase } from "@/lib/supabase";

export default function LoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });

  const update = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);

    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email: form.email,
      password: form.password,
    });

    if (authError) {
      setError("Invalid email or password. Please try again.");
      setLoading(false);
      return;
    }

    const userId = data.user?.id;
    if (!userId) {
      setError("Login failed. Please try again.");
      setLoading(false);
      return;
    }

    // Fetch installer profile to check role + onboarding status
    const { data: installer } = await supabase
      .from("installers")
      .select("role, contract_signed, stripe_payment_method_id")
      .eq("id", userId)
      .maybeSingle();

    setLoading(false);

    if (!installer) {
      navigate("/dashboard");
      return;
    }

    if (installer.role === "admin") {
      navigate("/admin");
      return;
    }

    // Gate: contract not signed → go to contract
    if (!installer.contract_signed) {
      navigate("/contract");
      return;
    }

    // Gate: no payment method → go to payment setup
    if (!installer.stripe_payment_method_id) {
      navigate("/payment-setup");
      return;
    }

    navigate("/dashboard");
  };

  return (
    <AuthLayout
      title="Welcome Back"
      subtitle="Sign in to your SolarLeadSource installer account."
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
            <i className="ri-error-warning-line"></i> {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={e => update("email", e.target.value)}
            placeholder="john@sunpowersolutions.com"
            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <Link to="/forgot-password" className="text-xs text-[#16a34a] hover:underline">Forgot password?</Link>
          </div>
          <input
            type="password"
            value={form.password}
            onChange={e => update("password", e.target.value)}
            placeholder="Your password"
            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i> Signing in...
            </span>
          ) : "Sign In"}
        </button>

        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-100"></div>
          </div>
          <div className="relative flex justify-center text-xs text-gray-400 bg-white px-3">or</div>
        </div>

        <p className="text-center text-sm text-gray-500">
          Don&apos;t have an account?{" "}
          <Link to="/signup" className="text-[#16a34a] font-medium hover:underline">Create one free</Link>
        </p>

        {/* Demo hint */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-700">
          <strong>Demo:</strong> Use any email starting with &quot;admin&quot; to access the admin panel. Any other email goes to the installer dashboard.
        </div>
      </form>
    </AuthLayout>
  );
}
